import { Box, Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { DynFormProps } from "..";
import { useCallback, useEffect } from "react";
import moment, { Moment } from "moment";
import { formatarDecimalNumber, formatarInputValorMonetárioSemPrefixo, formatarMonetárioParaNúmero } from "../../../helpers/masks";
import BasicDatePicker from "../../BasicDatePicker";

import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

export default function DynCotaCapital({ spec }: DynCotaCapitalProps) {
    const { control, watch, setValue, getValues } = useFormContext();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const parcelas: TParcela[] = watch(`${spec.name}.parcelas`) || [];

    useEffect(() => {
        const parcelas = watch(`${spec.name}.parcelas`) || [];

        const precisaPreencherDatas = parcelas.some((parcela: TParcela) => parcela.data_vencimento === null);

        if (precisaPreencherDatas) {
            const novasParcelas = parcelas.map((parcela: TParcela, i: number) => ({
                ...parcela,
                data_vencimento: i === 0
                    ? adicionarDiasUteis(moment(), 5) // Primeira parcela: 5 dias uteis a partir de hoje
                    : adicionarDiasUteis(moment(), 5).add(i, 'months').date(20), // Restante: dia 20 dos meses seguintes
            }));

            setValue(`${spec.name}.parcelas`, novasParcelas);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Função para recalcular as parcelas
    const recalcularParcelas = useCallback((novoValorTotal: string, qtdParcelas: number) => {
        const valorTotal = formatarMonetárioParaNúmero(novoValorTotal);
        const valorParcela = Math.floor((valorTotal / qtdParcelas) * 100) / 100;
        const resto = valorTotal - valorParcela * qtdParcelas;

        return Array.from({ length: qtdParcelas }, (_, i) => {
            let dataVencimento;

            if (parcelas[i]?.data_vencimento) {
                // Se já existe uma data e ela é futura, mantém
                const dataExistente = moment(parcelas[i].data_vencimento);
                if (dataExistente.isAfter(moment())) {
                    dataVencimento = dataExistente;
                } else {
                    // Se a data é anterior a hoje, recalcula
                    if (i === 0) {
                        dataVencimento = adicionarDiasUteis(moment(), 5);
                    } else {
                        dataVencimento = adicionarDiasUteis(moment(), 5).add(i, 'months').date(20);
                    }
                }
            } else {
                if (i === 0) {
                    dataVencimento = adicionarDiasUteis(moment(), 5);
                } else {
                    dataVencimento = adicionarDiasUteis(moment(), 5).add(i, 'months').date(20);
                }
            }

            return {
                numero_parcela: i + 1,
                valor_parcela: formatarInputValorMonetárioSemPrefixo(formatarDecimalNumber(i === qtdParcelas - 1 ? valorParcela + resto : valorParcela)),
                data_vencimento: dataVencimento
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Funções para manipulação de parcelas
    const handleDataChange = useCallback(async (index: number, novaData: Moment) => {
        const maxParcelas = parcelas.length - 1;
        const quantidadeParcelas = parcelas.length + 1;
        const datasAtuais = parcelas.map(p => moment(p.data_vencimento).format("YYYY-MM-DD"));
        datasAtuais[index] = novaData.format("YYYY-MM-DD");

        const dataLimiteUltimaParcela = adicionarDiasUteis(moment(), 5).add(maxParcelas, "months").endOf("month");

        const datasLimites: Moment[] = [];
        for (let i = 0; i < quantidadeParcelas; i++) {
            const diasRestantes = quantidadeParcelas - 1 - i;
            datasLimites[i] = moment(dataLimiteUltimaParcela).subtract(diasRestantes, "days");
        }

        // Atualizar as próximas parcelas com base na nova data
        for (let i = index + 1; i < datasAtuais.length; i++) {
            const vencimentoAnterior = moment(datasAtuais[i - 1]);
            const proximoVencimento = vencimentoAnterior.clone().add(1, "months");

            datasAtuais[i] = proximoVencimento.isAfter(datasLimites[i])
                ? datasLimites[i].format("YYYY-MM-DD")
                : proximoVencimento.format("YYYY-MM-DD");
        }

        // Atualizar as parcelas com as novas datas
        const novasParcelas = parcelas.map((parcela, i) => ({
            ...parcela,
            data_vencimento: moment(datasAtuais[i])
        }));

        setValue(`${spec.name}.parcelas`, novasParcelas);
    }, [parcelas, setValue, spec.name]);

    // ...existing code...

    // Função para manipulação de parcelas - atualizada para usar handleDataChange
    const handleAlterarDataVencimento = (index: number, data: Moment | null) => {
        if (data) {
            handleDataChange(index, data);
        }
    };

    const handleAdicionarParcela = () => {
        const qtdParcelas = parcelas.length + 1;
        const novasParcelas = recalcularParcelas(getValues(`${spec.name}.valor_total`), qtdParcelas);
        setValue(`${spec.name}.parcelas`, novasParcelas);
    };

    const handleDeletarParcela = () => {
        if (parcelas.length > 1) {
            const qtdParcelas = parcelas.length - 1;
            const novasParcelas = recalcularParcelas(getValues(`${spec.name}.valor_total`), qtdParcelas);
            setValue(`${spec.name}.parcelas`, novasParcelas);
        }
    };

    const calcularMinDate = (index: number) => {
        if (index <= 0) {
            return moment();
        }
        const dataAnterior = moment(parcelas[index - 1].data_vencimento);
        return dataAnterior.add(1, "days");
    };

    const calcularMaxDate = (index: number) => {
        const maxParcelas = parcelas.length - 1;
        if (index === parcelas.length - 1) {
            return adicionarDiasUteis(moment(), 5).add(maxParcelas, "months").endOf("month");
        }
        return adicionarDiasUteis(moment(), 5).add(maxParcelas, "months").endOf("month").subtract(parcelas.length - 1 - index, "days");
    };

    const adicionarDiasUteis = (data: moment.Moment, dias: number): moment.Moment => {
        let dataResultado = data.clone();
        let diasAdicionados = 0;

        while (diasAdicionados < dias) {
            dataResultado = dataResultado.add(1, 'day');
            // Pula sábado (6) e domingo (0)
            if (dataResultado.day() !== 0 && dataResultado.day() !== 6) {
                diasAdicionados++;
            }
        }

        return dataResultado;
    };

    return (
        <Grid container spacing={2} mb={2}>
            <Grid size={{ xs: 5 }}>
                <Controller
                    name={`${spec.name}.valor_total`}
                    rules={{
                        required: 'Campo obrigatório',
                        validate: (value) => {
                            if (value <= 0) return 'Valor total deve ser maior que zero';
                            const parcelas = getValues(`${spec.name}.parcelas`) || [];
                            if (formatarMonetárioParaNúmero(value) !== (parcelas.reduce((acc: any, curr: any) => acc + (formatarMonetárioParaNúmero(curr.valor_parcela) || 0), 0))) {
                                return 'Valor total das parcelas deve ser igual ao valor total';
                            }
                            return true;
                        }
                    }}
                    control={control}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <TextField
                            label="Total"
                            required
                            fullWidth
                            error={invalid}
                            helperText={error ? error.message : ""}
                            value={field.value}
                            onChange={(e) => {
                                const formatted = formatarInputValorMonetárioSemPrefixo(e.target.value);
                                field.onChange(formatted);
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography variant="body2" sx={{ marginRight: 1 }}>
                                                R$
                                            </Typography>
                                        </InputAdornment>
                                    )
                                }
                            }}
                            onBlur={() => {
                                const valorTotal = field.value || '0';
                                const qtdParcelas = parcelas.length || 1;
                                const novasParcelas = recalcularParcelas(valorTotal, qtdParcelas);
                                setValue(`${spec.name}.parcelas`, novasParcelas);
                            }}
                        />
                    )}
                />
            </Grid>
            {
                parcelas.length > 0 &&
                parcelas.map((parcela, index) => {
                    const minDate = calcularMinDate(index);
                    const maxDate = calcularMaxDate(index);
                    return (
                        <Grid container size={{ xs: 12 }} key={index} spacing={2}>
                            <Grid size={{ xs: 5 }}>
                                <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={2}>
                                    <Typography fontWeight={'bold'} variant="h6">{index + 1}°)</Typography>
                                    <Controller
                                        name={`${spec.name}.parcelas[${index}].data_vencimento`}
                                        rules={{
                                            required: 'Campo obrigatório',
                                            validate: (value) => {
                                                const parcelas: TParcela[] = getValues(`${spec.name}.parcelas`) || [];
                                                if (!value) {
                                                    return 'Data de vencimento é obrigatória';
                                                }
                                                const duplicateIndex = parcelas.findIndex(
                                                    (p, i) =>
                                                        i !== index &&
                                                        moment(p.data_vencimento).format('MM/YYYY') === moment(value).format('MM/YYYY')
                                                );
                                                if (duplicateIndex !== -1) {
                                                    return `Parcela ${duplicateIndex + 1} já possui vencimento no mesmo mês`;
                                                }
                                                return true;
                                            }
                                        }}
                                        control={control}
                                        render={({ field, fieldState: { error, invalid } }) => (
                                            <BasicDatePicker
                                                required
                                                label="Vencimento"
                                                blockPastDates
                                                blockFutureDates
                                                futureDates={maxDate}
                                                pastDates={minDate}
                                                value={field.value ? moment(field.value) : null}
                                                onChange={(data) => handleAlterarDataVencimento(index, data)}
                                                formatString="DD/MM/YYYY"
                                                error={invalid}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />
                                </Box>
                            </Grid>

                            <Grid size={{ xs: 5 }}>
                                <Box display={'flex'} alignItems={'center'} justifyContent={'center'} gap={2}>
                                    <Controller
                                        name={`${spec.name}.parcelas[${index}].valor_parcela`}
                                        rules={{
                                            required: 'Campo obrigatório',
                                            validate: (value) => {
                                                if (!value || value === '0') {
                                                    return 'Valor da parcela deve ser maior que zero';
                                                }
                                                return true;
                                            }
                                        }}
                                        control={control}
                                        render={({ field, fieldState: { error, invalid } }) => (
                                            <TextField
                                                label={`Valor`}
                                                required
                                                value={field.value}
                                                fullWidth
                                                onChange={(e) => {
                                                    const formattedValue = formatarInputValorMonetárioSemPrefixo(e.target.value);
                                                    field.onChange(formattedValue);
                                                }}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Typography variant="body2" sx={{ marginRight: 1 }}>
                                                                    R$
                                                                </Typography>
                                                            </InputAdornment>
                                                        )
                                                    }
                                                }}
                                                error={invalid}
                                                helperText={error ? error.message : ""}
                                            />
                                        )}
                                    />

                                    {
                                        parcela.numero_parcela !== parcelas.length &&
                                        <IconButton aria-label="delete" onClick={() => handleDeletarParcela()}>
                                            <DeleteIcon />
                                        </IconButton>
                                    }
                                    {
                                        parcela.numero_parcela === parcelas.length && parcela.numero_parcela < 6 &&
                                        <IconButton aria-label="delete" onClick={() => handleAdicionarParcela()}>
                                            <AddIcon />
                                        </IconButton>
                                    }
                                </Box>
                            </Grid>
                        </Grid>
                    )
                })
            }
        </Grid >
    )
}

interface DynCotaCapitalProps extends DynFormProps {
}

type TParcela = {
    numero_parcela: number,
    valor_parcela: string,
    data_vencimento: Moment,
}